import { FC, MouseEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { ExitToApp, KeyboardArrowDown, Login } from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import { AppBar, Box, Button, Container, IconButton, Menu, MenuItem, Toolbar } from '@mui/material';
import classNames from 'classnames';

import UserProfileModal from '@components/UserProfileModal/UserProfileModal';
import { useCategoriesAndResourcesNav } from '@hooks/useResources';
import { ICategory } from '@interfaces/resources';
import { INav } from '@interfaces/routes';
import { headerAdditionalNav, headerProfileNav, headerSupplementsNav } from '@routes/header';
import urls from '@routes/urls';
import { authTokenSelector } from '@store/features/auth/token/selectors';
import { userDataSelector } from '@store/features/auth/user/selectors';
import {
  canAccessAdminSelector,
  canAccessMedicationLookupSelector,
  canAccessSupplementInquirySelector,
  dashboardOrgDataSelector,
} from '@store/features/dashboardOrgInfo/selectors';
import getShortenEmail from '@utils/getShortenEmail';
import handleLogout from '@utils/handleLogout';

import AthleteSupplementsMenuItem from './AthleteSupplementsMenuItem/AthleteSupplementsMenuItem';
import HeaderDrawer from './HeaderDrawer/HeaderDrawer';

import './style.scss';

const Header: FC = () => {
  const authToken = useSelector(authTokenSelector);
  const user = useSelector(userDataSelector);
  const canAccessSupplementInquiry = useSelector(canAccessSupplementInquirySelector);
  const canAccessMedicationLookup = useSelector(canAccessMedicationLookupSelector);
  const canAccessAdmin = useSelector(canAccessAdminSelector);

  const dashboardOrgInfo = useSelector(dashboardOrgDataSelector);

  const infoPageTitle = dashboardOrgInfo?.infoPageTitle;

  const { t } = useTranslation();

  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);

  const [anchorAdditionalNav, setAnchorAdditionalNav] = useState<null | HTMLElement>(null);

  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

  const handleOpenUserMenu = (event: MouseEvent<HTMLElement>): void => {
    setAnchorElUser(event.currentTarget);
  };

  const handleOpenAdditionalNav = (event: MouseEvent<HTMLElement>): void => {
    setAnchorAdditionalNav(event.currentTarget);
  };

  const handleCloseUserMenu = (): void => {
    setAnchorElUser(null);
  };

  const handleCloseAdditionalNav = (): void => {
    setAnchorAdditionalNav(null);
  };

  const handleChangeDrawer = (): void => setIsDrawerOpen(!isDrawerOpen);
  const handleCloseDrawer = (): void => setIsDrawerOpen(false);

  const [isUserProfileModalOpen, setIUserProfileModalOpen] = useState<boolean>(false);

  const {
    categories,
    anchorResourcesNav,
    handleOpenResourcesNav,
    handleCloseResourcesNav,
    isResourcesActive,
  } = useCategoriesAndResourcesNav();

  const canShowRiskList = useMemo(() => {
    if (
      typeof user?.showRiskList === 'boolean' &&
      typeof dashboardOrgInfo?.showRiskList === 'boolean'
    ) {
      return user.showRiskList || dashboardOrgInfo.showRiskList;
    }
    return false;
  }, [user, dashboardOrgInfo]);

  const supplementsMenuLinks = useMemo(
    () =>
      headerSupplementsNav.filter((nav: INav) => {
        if (!canAccessSupplementInquiry && nav.path === urls.inquiryDraft) {
          return false;
        }
        if (!canShowRiskList && nav.path === urls.highRiskSupplements) {
          return false;
        }
        return true;
      }),
    [canShowRiskList, canAccessSupplementInquiry],
  );

  const canShowSupplementsMenu = useMemo(() => {
    if (!supplementsMenuLinks.length) {
      return false;
    }
    return Boolean(user && !user.isAdmin);
  }, [supplementsMenuLinks, user]);

  return (
    <>
      <UserProfileModal
        open={isUserProfileModalOpen}
        onClose={(): void => setIUserProfileModalOpen(false)}
      />

      <AppBar
        position="sticky"
        color="inherit"
        className="header"
        sx={{ zIndex: theme => theme.zIndex.drawer + 1 }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters className="header__toolbar">
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ display: { xs: 'flex', lg: 'none' } }} className="no-print-and-hide">
                <IconButton
                  size="large"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleChangeDrawer}
                  color="inherit"
                >
                  <MenuIcon />
                </IconButton>
              </Box>

              <NavLink to={urls.home}>
                <Box
                  component="img"
                  sx={{
                    height: { xs: 25, lg: 52 },
                    width: { xs: 84, lg: 160 },
                  }}
                  alt="AXIS - Drug Free Sport"
                  src="/images/logo.svg"
                />
              </NavLink>
            </Box>

            <Box sx={{ display: { xs: 'none', lg: 'flex' } }} className="header__main-nav">
              <NavLink key={urls.home} to={urls.home} className="no-print-and-hide">
                {t('headerNav.home')}
              </NavLink>

              {canShowSupplementsMenu && (
                <AthleteSupplementsMenuItem menuLinks={supplementsMenuLinks} />
              )}

              {canAccessMedicationLookup && (
                <NavLink key={urls.medications} to={urls.medications} className="no-print-and-hide">
                  {t('headerNav.medicationStatusLookup')}
                </NavLink>
              )}

              {infoPageTitle && (
                <NavLink to={urls.infoPage} className="no-print-and-hide">
                  {infoPageTitle}
                </NavLink>
              )}

              {canAccessAdmin && (
                <NavLink to={urls.adminInquiries} className="no-print-and-hide">
                  {t('headerNav.adminInquiries')}
                </NavLink>
              )}

              {user && !user.isAdmin && (
                <Button
                  className={classNames(
                    'header__link-w-chevron',
                    'header__resources-nav',
                    'no-print-and-hide',
                    isResourcesActive && 'header__active-link',
                  )}
                  variant="text"
                  onClick={handleOpenResourcesNav}
                  sx={{ p: 0 }}
                  endIcon={<KeyboardArrowDown color="primary" className="header__chevron" />}
                >
                  {t('headerNav.resources')}
                </Button>
              )}

              {categories && (
                <Menu
                  sx={{ mt: '45px' }}
                  className="header__categories-nav no-print-and-hide"
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  anchorEl={anchorResourcesNav}
                  id="resources-nav"
                  open={Boolean(anchorResourcesNav)}
                  onClose={handleCloseResourcesNav}
                >
                  {categories.map((category: ICategory) => (
                    <MenuItem key={category.id} onClick={handleCloseResourcesNav}>
                      <NavLink
                        to={{
                          pathname: `/category/${category.id}`,
                        }}
                      >
                        {category.name}
                      </NavLink>
                    </MenuItem>
                  ))}
                </Menu>
              )}

              {canAccessAdmin && (
                <NavLink to={urls.adminHome} end className="no-print-and-hide">
                  {t('headerNav.admin')}
                </NavLink>
              )}

              <Button
                className="header__link-w-chevron no-print-and-hide header__about-button"
                variant="text"
                onClick={handleOpenAdditionalNav}
                sx={{ p: 0 }}
                endIcon={<KeyboardArrowDown color="primary" className="header__chevron" />}
              >
                {t('headerNav.about')}
              </Button>

              <Menu
                sx={{ mt: '45px' }}
                className="header__additional-nav no-print-and-hide"
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                anchorEl={anchorAdditionalNav}
                id="additional-nav"
                open={Boolean(anchorAdditionalNav)}
                onClose={handleCloseAdditionalNav}
              >
                {headerAdditionalNav.map((nav: INav) => (
                  <MenuItem key={nav.path} onClick={handleCloseAdditionalNav}>
                    {/* eslint-disable-next-line react/jsx-no-target-blank */}
                    <a target="_blank" href={nav.path}>
                      {t(nav.i18nTitle)}
                    </a>
                  </MenuItem>
                ))}
              </Menu>
            </Box>

            <Box
              sx={{ display: 'flex', flexGrow: 0, flexDirection: 'row' }}
              className="no-print-and-hide"
            >
              <Box className="header__profile-email" sx={{ display: { xs: 'none', lg: 'flex' } }}>
                {user && (
                  <Button
                    className="header__link-w-chevron"
                    variant="text"
                    onClick={handleOpenUserMenu}
                    sx={{ p: 0 }}
                    endIcon={<KeyboardArrowDown color="primary" className="header__chevron" />}
                  >
                    {user?.email ? getShortenEmail(user.email) : ''}
                  </Button>
                )}
              </Box>

              <Menu
                sx={{ mt: '45px' }}
                id="profile-nav"
                className="header__profile-nav"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                {headerProfileNav.map((nav: INav) => {
                  if (!canAccessSupplementInquiry && nav.path === urls.inquiries) {
                    return null;
                  }

                  return (
                    <MenuItem
                      key={nav.path}
                      className="header__profile-nav-link"
                      onClick={(): void => {
                        handleCloseAdditionalNav();
                        handleCloseUserMenu();
                      }}
                    >
                      <NavLink to={nav.path}>{t(nav.i18nTitle)}</NavLink>
                    </MenuItem>
                  );
                })}

                {user && (
                  <MenuItem
                    onClick={(): void => {
                      setIUserProfileModalOpen(true);
                      handleCloseUserMenu();
                    }}
                  >
                    {t('headerProfileNav.profile')}
                  </MenuItem>
                )}

                {authToken ? (
                  <MenuItem
                    onClick={() => {
                      handleCloseUserMenu();
                      handleLogout();
                    }}
                  >
                    <ExitToApp />
                    {t('common.auth.logout')}
                  </MenuItem>
                ) : (
                  <MenuItem>
                    <Login />
                    <NavLink to={urls.login}>{t('common.auth.login')}</NavLink>
                  </MenuItem>
                )}
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <HeaderDrawer
        canShowSupplementsMenu={canShowSupplementsMenu}
        supplementsMenuLinks={supplementsMenuLinks}
        open={isDrawerOpen}
        onClose={handleCloseDrawer}
      />
    </>
  );
};

export default Header;
